/**
 * Production builder for the document-preview fact list.
 *
 * Extracted from ``DocumentsPage.previewFacts`` (#1527 codex P3) so the page→preview
 * mapping is a single, directly-testable function rather than an inline memo a test can
 * only re-implement. The memo now delegates here, so a regression that drops the
 * ``line_items`` → Buchungen-table wiring fails the unit test on this function.
 */
import type { PreviewFact } from '@hofsync/design-system';
import type { DocumentMetadata } from '../../services/documentService';
import { formatFactValue } from './factValueDisplay';
import { buildLineItemsTableExtras } from './lineItemsTable';

/**
 * Map a document's metadata to the ordered ``PreviewFact[]`` the ``DocumentPreview``
 * renders, or ``null`` when there are no extracted facts. A v3 repeating-row field
 * (``line_items`` on a finance/bank-doc cell) is wired to the non-editable Buchungen
 * table (``rows``/``columns``); every other fact stays a scalar value row.
 */
export function buildPreviewFacts(metadata: DocumentMetadata | null | undefined): PreviewFact[] | null {
    if (!metadata?.extracted_facts) return null;
    const validationBlock = metadata._validation ?? {};
    // 37 taxonomy-v2 cells declare an EXTRACTED field literally named ``document_date``
    // (``fields/delivery-note.yaml:888``, label "Datum"), which collides head-on with the
    // synthetic row below. Two PreviewFacts sharing a ``field`` is not cosmetic:
    // ``DocumentPreview`` uses ``key={fact.field}`` and gates its inline editor on
    // ``editingField === fact.field``, so BOTH rows opened an editor at once over one shared
    // ``editValue``/``editInputRef`` — committing either blurred the other and
    // ``handleFactEditSave`` clears ``editValue`` before the second commit lands, so the save
    // posted "". Fold the declared field into the synthetic row instead of emitting it twice:
    // the column is what the archive list groups on and what the ``type: 'date'`` input
    // round-trips, so it wins the value, but the extracted entry's ``overridden`` flag is real
    // provenance and must survive.
    const declaredDocumentDate = metadata.extracted_facts['document_date'];
    const facts: PreviewFact[] = [
        {
            field: 'document_date',
            label: 'Dokumentdatum',
            value: metadata.document_date ?? '',
            type: 'date',
            validation: validationBlock['document_date'],
            ...(declaredDocumentDate?.overridden !== undefined
                ? { overridden: declaredDocumentDate.overridden }
                : {}),
        },
    ];
    facts.push(
        ...Object.entries(metadata.extracted_facts)
            .filter(([field]) => field !== 'document_date')
            .map(([field, info]) => {
                const fact: PreviewFact = {
                    field,
                    label: info.label || field,
                    // A v3 repeating-row field (``line_items``, #1498) arrives as a raw array of row objects;
                    // formatFactValue summarises it ("N Positionen") as the scalar/a11y fallback.
                    value: formatFactValue(info.value),
                    overridden: info.overridden,
                    validation: validationBlock[field],
                };
                // Only the bank-statement cell's (finance/bank-doc) ``line_items`` gets the
                // Buchungen table — its row shape matches the bank column model. ``line_items``
                // on any other cell (finance/invoice, livestock/*, …) has a different shape and
                // keeps the scalar "N Positionen" summary (#1525 codex P1). A table branch has no
                // scalar edit affordance, so this is also what makes line_items non-editable (#1527).
                const tableExtras = buildLineItemsTableExtras(field, info.value, metadata.classification);
                if (tableExtras) {
                    fact.rows = tableExtras.rows;
                    fact.columns = tableExtras.columns;
                }
                return fact;
            }),
    );
    return facts;
}
